import { useCallback, useEffect, useState, Fragment } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Inbox, RefreshCw, Search, ChevronLeft, ChevronRight, ChevronDown, ChevronRight as ChevronRightIcon } from "lucide-react";
import { toast } from "sonner";

const PAGE_SIZE = 25;
const ALL = "__all__"; // sentinel: Radix Select non accetta value="" per <SelectItem>

// Mapping etichette source
const SOURCE_LABEL = {
  landing: "Demo",
  onboarding: "Onboarding",
  aiuto: "Assistenza",
};
const SOURCE_CLASSES = {
  landing: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  onboarding: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  aiuto: "bg-amber-500/15 text-amber-300 border-amber-500/30",
};
const URGENZA_CLASSES = {
  Alta: "bg-red-500/20 text-red-300 border-red-500/40",
  Media: "bg-amber-500/20 text-amber-200 border-amber-500/40",
  Bassa: "bg-slate-500/20 text-slate-300 border-slate-500/40",
};

function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", { timeZone: "Europe/Rome" });
  } catch { return iso; }
}

function Badge({ children, className = "" }) {
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${className}`}>
      {children}
    </span>
  );
}

export default function MasterRichieste() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ source: "", argomento: "", urgenza: "", q: "" });
  const [qInput, setQInput] = useState("");
  const [expanded, setExpanded] = useState(null); // row id espansa

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("limit", String(PAGE_SIZE));
      if (filters.source) params.set("source", filters.source);
      if (filters.argomento) params.set("argomento", filters.argomento);
      if (filters.urgenza) params.set("urgenza", filters.urgenza);
      if (filters.q) params.set("q", filters.q);
      const res = await api.get(`/master/support-calls?${params.toString()}`);
      setRows(res.data?.items || []);
      setTotal(res.data?.total || 0);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => { load(); }, [load]);

  const applySearch = () => {
    setPage(1);
    setFilters((f) => ({ ...f, q: qInput.trim() }));
  };
  const resetFilters = () => {
    setPage(1);
    setQInput("");
    setFilters({ source: "", argomento: "", urgenza: "", q: "" });
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-wider text-amber-400 font-semibold">Supporto</div>
          <h1 className="font-heading text-3xl font-bold">Richieste call ricevute</h1>
          <p className="text-sm text-slate-400 mt-1">
            Prenotazioni demo, richieste di configurazione onboarding e ticket di assistenza — tutto in un&apos;unica coda ordinata dalla più recente.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={load}
          className="border-slate-700 bg-slate-900 text-slate-300 hover:bg-slate-800"
          data-testid="master-richieste-refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 mr-2 ${loading ? "animate-spin" : ""}`} /> Aggiorna
        </Button>
      </div>

      {/* Filtri */}
      <Card className="bg-slate-900 border-slate-800 p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Tipo</label>
            <Select
              value={filters.source || ALL}
              onValueChange={(v) => { setPage(1); setFilters((f) => ({ ...f, source: v === ALL ? "" : v })); }}
            >
              <SelectTrigger className="bg-slate-950 border-slate-700 text-slate-200" data-testid="filter-source">
                <SelectValue placeholder="Tutti" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Tutti</SelectItem>
                <SelectItem value="landing">Demo</SelectItem>
                <SelectItem value="onboarding">Onboarding</SelectItem>
                <SelectItem value="aiuto">Assistenza</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Argomento</label>
            <Select
              value={filters.argomento || ALL}
              onValueChange={(v) => { setPage(1); setFilters((f) => ({ ...f, argomento: v === ALL ? "" : v })); }}
            >
              <SelectTrigger className="bg-slate-950 border-slate-700 text-slate-200" data-testid="filter-argomento">
                <SelectValue placeholder="Tutti" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Tutti</SelectItem>
                <SelectItem value="Incassi e foglio cassa">Incassi e foglio cassa</SelectItem>
                <SelectItem value="Versamenti">Versamenti</SelectItem>
                <SelectItem value="Report PDF/Excel">Report PDF/Excel</SelectItem>
                <SelectItem value="Email e notifiche">Email e notifiche</SelectItem>
                <SelectItem value="Pagamenti e abbonamento">Pagamenti e abbonamento</SelectItem>
                <SelectItem value="Altro">Altro</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Urgenza</label>
            <Select
              value={filters.urgenza || ALL}
              onValueChange={(v) => { setPage(1); setFilters((f) => ({ ...f, urgenza: v === ALL ? "" : v })); }}
            >
              <SelectTrigger className="bg-slate-950 border-slate-700 text-slate-200" data-testid="filter-urgenza">
                <SelectValue placeholder="Tutte" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Tutte</SelectItem>
                <SelectItem value="Alta">Alta</SelectItem>
                <SelectItem value="Media">Media</SelectItem>
                <SelectItem value="Bassa">Bassa</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Cerca</label>
            <div className="flex gap-2">
              <Input
                value={qInput}
                onChange={(e) => setQInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && applySearch()}
                placeholder="Nome, email, agenzia, note…"
                className="bg-slate-950 border-slate-700 text-slate-200 placeholder:text-slate-500"
                data-testid="filter-q"
              />
              <Button
                size="sm"
                onClick={applySearch}
                className="bg-amber-500 text-slate-900 hover:bg-amber-400 shrink-0"
                data-testid="filter-q-submit"
              >
                <Search className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between text-xs text-slate-400">
          <div>{total} richieste totali{filters.q || filters.source || filters.argomento || filters.urgenza ? " (con i filtri attivi)" : ""}</div>
          {(filters.q || filters.source || filters.argomento || filters.urgenza) && (
            <button
              type="button"
              onClick={resetFilters}
              className="text-amber-400 hover:text-amber-300 underline"
              data-testid="filter-reset"
            >
              Rimuovi filtri
            </button>
          )}
        </div>
      </Card>

      {/* Lista */}
      <Card className="bg-slate-900 border-slate-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-800/60 text-left">
                <th className="w-8"></th>
                <th className="px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Data</th>
                <th className="px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Tipo</th>
                <th className="px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Richiedente</th>
                <th className="px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Contesto</th>
                <th className="px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Contatti</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center text-slate-400 py-10">
                    <Inbox className="w-6 h-6 mx-auto mb-2 text-slate-600" />
                    Nessuna richiesta trovata
                  </td>
                </tr>
              )}
              {rows.map((r) => {
                const isOpen = expanded === r.id;
                return (
                  <Fragment key={r.id}>
                    <tr
                      className="hover:bg-slate-800/40 cursor-pointer"
                      onClick={() => setExpanded(isOpen ? null : r.id)}
                      data-testid={`row-${r.id}`}
                    >
                      <td className="pl-3 text-slate-500">
                        {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRightIcon className="w-4 h-4" />}
                      </td>
                      <td className="px-4 py-3 text-slate-300 font-mono text-xs whitespace-nowrap">
                        {formatDateTime(r.created_at)}
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={SOURCE_CLASSES[r.source] || "bg-slate-700/30 text-slate-300 border-slate-600/40"}>
                          {SOURCE_LABEL[r.source] || r.source || "—"}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-slate-100 font-medium">{r.nome || "—"}</div>
                        {r.agenzia && <div className="text-xs text-slate-400">{r.agenzia}</div>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap items-center gap-1">
                          {r.argomento && (
                            <span className="text-xs text-slate-300 bg-slate-800/60 border border-slate-700 rounded px-1.5 py-0.5">
                              {r.argomento}
                            </span>
                          )}
                          {r.urgenza && (
                            <Badge className={URGENZA_CLASSES[r.urgenza] || "bg-slate-700/40 text-slate-300 border-slate-600/40"}>
                              {r.urgenza}
                            </Badge>
                          )}
                          {r.numero_collaboratori && (
                            <span className="text-xs text-slate-400">👥 {r.numero_collaboratori}</span>
                          )}
                          {(r.configurazioni_richieste || []).length > 0 && (
                            <span className="text-xs text-emerald-400">
                              {r.configurazioni_richieste.length} aree
                            </span>
                          )}
                          {!r.argomento && !r.urgenza && !r.numero_collaboratori && !(r.configurazioni_richieste || []).length && (
                            <span className="text-xs text-slate-500">—</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-xs">
                          <a
                            href={`mailto:${r.email}`}
                            className="text-amber-400 hover:text-amber-300"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {r.email}
                          </a>
                          {r.telefono && <div className="text-slate-400 mt-0.5">{r.telefono}</div>}
                        </div>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-slate-950/70">
                        <td></td>
                        <td colSpan={5} className="px-4 py-4 text-xs text-slate-300">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Note del richiedente</div>
                              <div className="whitespace-pre-wrap text-slate-200 bg-slate-900 border border-slate-800 rounded p-3 min-h-[60px]">
                                {r.note?.trim() || <span className="text-slate-500">—</span>}
                              </div>
                            </div>
                            <div className="space-y-2">
                              {r.fascia_oraria && (
                                <div>
                                  <span className="text-[10px] uppercase tracking-wider text-slate-500">Fascia oraria: </span>
                                  <span className="text-slate-200">{r.fascia_oraria}</span>
                                </div>
                              )}
                              {r.come_ci_hai_conosciuto && (
                                <div>
                                  <span className="text-[10px] uppercase tracking-wider text-slate-500">Canale: </span>
                                  <span className="text-slate-200">{r.come_ci_hai_conosciuto}</span>
                                </div>
                              )}
                              {(r.configurazioni_richieste || []).length > 0 && (
                                <div>
                                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Aree da configurare</div>
                                  <ul className="list-disc list-inside text-slate-200 space-y-0.5">
                                    {r.configurazioni_richieste.map((c) => (<li key={c}>{c}</li>))}
                                  </ul>
                                </div>
                              )}
                              <div className="text-[10px] text-slate-500 pt-2 font-mono">
                                ID: {r.id} · IP: {r.ip || "—"}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Paginazione */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-800 text-sm">
            <div className="text-slate-400">
              Pagina <span className="text-slate-200 font-semibold">{page}</span> di {totalPages}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="border-slate-700 bg-slate-900 text-slate-300 hover:bg-slate-800 disabled:opacity-40"
                data-testid="page-prev"
              >
                <ChevronLeft className="w-3.5 h-3.5" /> Precedente
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="border-slate-700 bg-slate-900 text-slate-300 hover:bg-slate-800 disabled:opacity-40"
                data-testid="page-next"
              >
                Successiva <ChevronRight className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
